import { registerPageInit } from '../barba/page-init-registry';
import '../../css/_root.scss';
import '../../css/pages/reservation/reservation.scss';
import { FormWidget } from '../widgets/form-widget';
import { initializePhoneMask } from '@/js/utils/initializePhoneMask';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { beforeSubmit } from '@/js/utils/beforeSubmit';
import { trackYaGoal } from '@/js/utils/yandex-metrika';
import notificationsWordings from '@/wordings/notificationWordings.json';

class ResidencyPage {
  private reservationFormWidget: FormWidget;
  private datetimeValues: {
    from: { date: string; hour: string; minute: string; full: string };
    to: { date: string; hour: string; minute: string; full: string };
  };

  constructor() {
    const { reservationFormWordings } = notificationsWordings;

    const successMessageEl = document.createElement('div');
    const errorMessageEl = document.createElement('div');
    successMessageEl.innerHTML = `<p class='toast-template__title'>${reservationFormWordings.title}</p><p class='toast-template__description'>${reservationFormWordings.description}</p>`;
    errorMessageEl.innerHTML = `<p class='toast-template__title'>${reservationFormWordings.errorTitle}</p><p class='toast-template__description'>${reservationFormWordings.errorDescription}</p>`;

    this.reservationFormWidget = new FormWidget({
      formSelector: '[id^="reservationForm"]',
      submitUrl: getApiUrl('/api/reservationForm.php'),
      successMessage: successMessageEl,
      errorMessage: errorMessageEl,
      beforeSubmit: beforeSubmit,
      onSuccess: () => {
        trackYaGoal('submitApplication');
      },
    });

    this.datetimeValues = {
      from: { date: '', hour: '', minute: '', full: '' },
      to: { date: '', hour: '', minute: '', full: '' },
    };
  }

  public init(): void {
    this.reservationFormWidget.init();
    this.bindEvents();
    initializePhoneMask('phone');
  }

  private bindEvents(): void {
    const datetimeInputFrom = document.getElementById('datetimeInputFrom') as HTMLInputElement;

    if (!datetimeInputFrom) {
      // На этой странице нет datetime-виджета — выходим
      return;
    }

    // DOM elements
    const datetimeInputTo = document.getElementById('datetimeInputTo') as HTMLInputElement;

    const toggleDropdownBtnFrom = document.getElementById(
      'toggleDropdownBtnFrom',
    ) as HTMLSpanElement;
    const toggleDropdownBtnTo = document.getElementById('toggleDropdownBtnTo') as HTMLSpanElement;

    const datetimeDropdownFrom = document.getElementById('datetimeDropdownFrom') as HTMLDivElement;
    const datetimeDropdownTo = document.getElementById('datetimeDropdownTo') as HTMLDivElement;

    const dateInputFrom = document.getElementById('dateInputFrom') as HTMLInputElement;
    const dateInputTo = document.getElementById('dateInputTo') as HTMLInputElement;

    const hourSelectFrom = document.getElementById('hourSelectFrom') as HTMLSelectElement;
    const hourSelectTo = document.getElementById('hourSelectTo') as HTMLSelectElement;

    const minuteSelectFrom = document.getElementById('minuteSelectFrom') as HTMLSelectElement;
    const minuteSelectTo = document.getElementById('minuteSelectTo') as HTMLSelectElement;

    const applyBtnFrom = document.getElementById('applyBtnFrom') as HTMLButtonElement;
    const applyBtnTo = document.getElementById('applyBtnTo') as HTMLButtonElement;

    const cancelBtnFrom = document.getElementById('cancelBtnFrom') as HTMLButtonElement;
    const cancelBtnTo = document.getElementById('cancelBtnTo') as HTMLButtonElement;

    const datetimeFromError = document.getElementById('datetimeFromError') as HTMLDivElement;
    const datetimeToError = document.getElementById('datetimeToError') as HTMLDivElement;

    // Utility functions
    const clearMessage = (el: HTMLElement): void => {
      el.textContent = '';
      el.className = '';
    };

    const showHint = (el: HTMLElement, message: string): void => {
      el.textContent = message;
      el.className = 'text-info mt-1'; // или ваш BEM-класс, например 'reservation-form__hint'
    };

    const showError = (el: HTMLElement, message: string): void => {
      el.textContent = message;
      el.className = 'text-danger mt-1'; // или 'reservation-form__error'
    };

    // Hidden fields
    const addHiddenFields = (): void => {
      const form = document.getElementById('reservationForm');
      if (!form) return;

      if (!document.getElementById('datetimeStartHidden')) {
        const hiddenStart = document.createElement('input');
        hiddenStart.type = 'hidden';
        hiddenStart.id = 'datetimeStartHidden';
        hiddenStart.name = 'datetime_start';
        form.appendChild(hiddenStart);
      }

      if (!document.getElementById('datetimeEndHidden')) {
        const hiddenEnd = document.createElement('input');
        hiddenEnd.type = 'hidden';
        hiddenEnd.id = 'datetimeEndHidden';
        hiddenEnd.name = 'datetime_end';
        form.appendChild(hiddenEnd);
      }
    };

    const updateFormFields = (): void => {
      const hiddenStart = document.getElementById('datetimeStartHidden') as HTMLInputElement;
      const hiddenEnd = document.getElementById('datetimeEndHidden') as HTMLInputElement;

      if (hiddenStart) hiddenStart.value = this.datetimeValues.from.full;
      if (hiddenEnd) hiddenEnd.value = this.datetimeValues.to.full;
    };

    // Control state of "to" field based on "from"
    const updateEndDateFieldState = (): void => {
      const isFromSelected = !!this.datetimeValues.from.full;
      datetimeInputTo.disabled = !isFromSelected;
      toggleDropdownBtnTo.style.pointerEvents = isFromSelected ? 'auto' : 'none';
      toggleDropdownBtnTo.style.opacity = isFromSelected ? '1' : '0.6';
    };

    // Initialize hidden fields
    addHiddenFields();

    // Dropdown toggling
    const toggleDropdownFrom = (): void => {
      if (datetimeInputFrom.disabled) return;
      datetimeDropdownFrom.classList.toggle('show');
      if (datetimeDropdownFrom.classList.contains('show')) {
        initializeCurrentTimeFrom();
      }
    };

    const toggleDropdownTo = (): void => {
      if (datetimeInputTo.disabled) return;
      datetimeDropdownTo.classList.toggle('show');
      if (datetimeDropdownTo.classList.contains('show')) {
        initializeCurrentTimeTo();
      }
    };

    const closeDropdownFrom = (): void => {
      datetimeDropdownFrom.classList.remove('show');
    };

    const closeDropdownTo = (): void => {
      datetimeDropdownTo.classList.remove('show');
    };

    // Initialize current time
    const initializeCurrentTimeFrom = (): void => {
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0];
      if (!dateInputFrom.value) dateInputFrom.value = formattedDate;
      if (!hourSelectFrom.value)
        hourSelectFrom.value = today.getHours().toString().padStart(2, '0');
      if (!minuteSelectFrom.value) {
        const minutes = Math.round(today.getMinutes() / 5) * 5;
        minuteSelectFrom.value = minutes.toString().padStart(2, '0');
      }
    };

    const initializeCurrentTimeTo = (): void => {
      const today = new Date();
      const baseDate = this.datetimeValues.from.date || today.toISOString().split('T')[0];
      if (!dateInputTo.value) dateInputTo.value = baseDate;

      if (this.datetimeValues.from.hour && this.datetimeValues.from.minute) {
        const start = new Date(
          `${this.datetimeValues.from.date}T${this.datetimeValues.from.hour}:${this.datetimeValues.from.minute}`,
        );
        const minEnd = new Date(start.getTime() + 2 * 60 * 60 * 1000);
        const h = minEnd.getHours().toString().padStart(2, '0');
        const m = Math.min(Math.ceil(minEnd.getMinutes() / 5) * 5, 55)
          .toString()
          .padStart(2, '0');
        hourSelectTo.value = h;
        minuteSelectTo.value = m;
      } else {
        if (!hourSelectTo.value)
          hourSelectTo.value = ((today.getHours() + 2) % 24).toString().padStart(2, '0');
        if (!minuteSelectTo.value) minuteSelectTo.value = '00';
      }
    };

    // Handlers
    const applyHandlerFrom = (): void => {
      clearMessage(datetimeFromError);
      const dateValue = dateInputFrom.value;
      const hourValue = hourSelectFrom.value;
      const minuteValue = minuteSelectFrom.value;

      if (!dateValue || !hourValue || !minuteValue) {
        showError(datetimeFromError, 'Заполните все поля: дату, часы и минуты');
        return;
      }

      const formattedDate = dateValue.split('-').reverse().join('.');
      const result = `${formattedDate} ${hourValue}:${minuteValue}`;

      datetimeInputFrom.value = result;
      this.datetimeValues.from = {
        date: dateValue,
        hour: hourValue,
        minute: minuteValue,
        full: result,
      };
      updateFormFields();
      closeDropdownFrom();

      // === СБРАСЫВАЕМ ОКОНЧАНИЕ ПРИ ЛЮБОМ ИЗМЕНЕНИИ НАЧАЛА ===
      datetimeInputTo.value = '';
      this.datetimeValues.to = { date: '', hour: '', minute: '', full: '' };
      updateFormFields();

      // Показываем подсказку
      showHint(datetimeToError, 'Начало мероприятия изменено. Выберите дату и время окончания.');

      // Обновляем состояние и min-дату
      updateEndDateFieldState();
      dateInputTo.min = dateValue;
    };

    const applyHandlerTo = (): void => {
      // Пользователь взаимодействует — убираем подсказку/ошибку
      clearMessage(datetimeToError);

      const dateValue = dateInputTo.value;
      const hourValue = hourSelectTo.value;
      const minuteValue = minuteSelectTo.value;

      if (!dateValue || !hourValue || !minuteValue) {
        showError(datetimeToError, 'Заполните все поля: дату, часы и минуты');
        return;
      }

      if (!this.datetimeValues.from.date) {
        showError(datetimeToError, 'Сначала укажите время начала');
        return;
      }

      const startDate = new Date(
        `${this.datetimeValues.from.date}T${this.datetimeValues.from.hour}:${this.datetimeValues.from.minute}`,
      );
      const endDate = new Date(`${dateValue}T${hourValue}:${minuteValue}`);

      // Проверка: окончание должно быть строго позже начала (с учётом дат!)
      if (endDate <= startDate) {
        showError(datetimeToError, 'Дата и время окончания должны быть позже начала');
        return;
      }

      // Минимум 2 часа (7200000 мс)
      const minDurationMs = 2 * 60 * 60 * 1000;
      if (endDate.getTime() - startDate.getTime() < minDurationMs) {
        showError(datetimeToError, 'Минимальная продолжительность — 2 часа');
        return;
      }

      const formattedDate = dateValue.split('-').reverse().join('.');
      const result = `${formattedDate} ${hourValue}:${minuteValue}`;

      datetimeInputTo.value = result;
      this.datetimeValues.to = {
        date: dateValue,
        hour: hourValue,
        minute: minuteValue,
        full: result,
      };
      updateFormFields();
      closeDropdownTo();
    };

    const cancelHandlerFrom = (): void => {
      dateInputFrom.value = '';
      hourSelectFrom.value = '';
      minuteSelectFrom.value = '';
      datetimeInputFrom.value = '';
      this.datetimeValues.from = { date: '', hour: '', minute: '', full: '' };
      updateFormFields();
      clearMessage(datetimeFromError);
      closeDropdownFrom();
      updateEndDateFieldState();

      // Также сбрасываем окончание и очищаем его сообщение
      datetimeInputTo.value = '';
      this.datetimeValues.to = { date: '', hour: '', minute: '', full: '' };
      updateFormFields();
      clearMessage(datetimeToError);
    };

    const cancelHandlerTo = (): void => {
      dateInputTo.value = '';
      hourSelectTo.value = '';
      minuteSelectTo.value = '';
      datetimeInputTo.value = '';
      this.datetimeValues.to = { date: '', hour: '', minute: '', full: '' };
      updateFormFields();
      clearMessage(datetimeToError);
      closeDropdownTo();
    };

    // Event listeners
    toggleDropdownBtnFrom.addEventListener('click', toggleDropdownFrom);
    toggleDropdownBtnTo.addEventListener('click', toggleDropdownTo);

    datetimeInputFrom.addEventListener('click', toggleDropdownFrom);
    datetimeInputTo.addEventListener('click', toggleDropdownTo);

    applyBtnFrom.addEventListener('click', applyHandlerFrom.bind(this));
    applyBtnTo.addEventListener('click', applyHandlerTo.bind(this));

    cancelBtnFrom.addEventListener('click', cancelHandlerFrom.bind(this));
    cancelBtnTo.addEventListener('click', cancelHandlerTo.bind(this));

    // Close dropdowns on outside click
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;

      if (
        datetimeDropdownFrom.classList.contains('show') &&
        !datetimeDropdownFrom.contains(target) &&
        !datetimeInputFrom.contains(target) &&
        !toggleDropdownBtnFrom.contains(target)
      ) {
        closeDropdownFrom();
      }

      if (
        datetimeDropdownTo.classList.contains('show') &&
        !datetimeDropdownTo.contains(target) &&
        !datetimeInputTo.contains(target) &&
        !toggleDropdownBtnTo.contains(target)
      ) {
        closeDropdownTo();
      }
    };

    document.addEventListener('click', handleClickOutside);

    dateInputFrom.min = new Date().toISOString().split('T')[0];
    updateEndDateFieldState();
  }

}

registerPageInit('reservation', () => {
  const page = new ResidencyPage();
  page.init();
});
